import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';

interface TomaInfo {
  tomaId: number;
  fecha: string;
  tableName: string;
}

interface MatrixCell {
  value: number;
  count: number;
}

interface SkuData {
  sku: string;
  price: number;
  imagen?: string | null;
}

interface CategoryPriceTrendData {
  tomas: TomaInfo[];
  categories: string[];
  matrix: { [category: string]: { [tomaId: number]: MatrixCell | null } };
  skuMatrix?: { [category: string]: { [tomaId: number]: SkuData[] } } | null;
  analysisLevel: 'listings' | 'skus';
  metric: 'average' | 'median';
  priceType: 'current' | 'original';
  baselineTomaId: number | null;
}

interface CategoryPriceMatrixProps {
  data: CategoryPriceTrendData;
  formatPrice: (price: number) => string;
  formatDate: (dateStr: string) => string;
  expandSkus?: boolean;
  analysisLevel: 'listings' | 'skus';
  getUnitLabel: (count: number, plural?: boolean) => string;
}

const CategoryPriceMatrix: React.FC<CategoryPriceMatrixProps> = ({
  data,
  formatPrice,
  formatDate,
  expandSkus = false,
  analysisLevel,
  getUnitLabel
}) => {
  const { t } = useTranslation();

  // State for expanded categories (when showing SKUs)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // State for search
  const [searchTerm, setSearchTerm] = useState('');

  // State for pagination
  const [visibleCategories, setVisibleCategories] = useState(20);

  // Filter categories by search
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return data.categories;
    const lowerSearch = searchTerm.toLowerCase();
    return data.categories.filter(cat => cat.toLowerCase().includes(lowerSearch));
  }, [data.categories, searchTerm]);

  // Get displayed categories (with pagination)
  const displayedCategories = filteredCategories.slice(0, visibleCategories);

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  // Calculate price change color
  const getCellColor = (
    category: string,
    currentTomaId: number,
    currentIndex: number
  ): string => {
    const currentCell = data.matrix[category]?.[currentTomaId];
    if (!currentCell) return 'bg-gray-100';

    // Find previous toma with data
    let prevCell: MatrixCell | null = null;
    for (let i = currentIndex - 1; i >= 0; i--) {
      const prevTomaId = data.tomas[i].tomaId;
      if (data.matrix[category]?.[prevTomaId]) {
        prevCell = data.matrix[category][prevTomaId];
        break;
      }
    }

    if (!prevCell) return 'bg-gray-50'; // No previous data

    const currentValue = currentCell.value;
    const prevValue = prevCell.value;

    if (currentValue < prevValue) {
      // Price decreased - green
      const decrease = ((prevValue - currentValue) / prevValue) * 100;
      if (decrease > 10) return 'bg-green-200';
      if (decrease > 5) return 'bg-green-100';
      return 'bg-green-50';
    } else if (currentValue > prevValue) {
      // Price increased - red/orange
      const increase = ((currentValue - prevValue) / prevValue) * 100;
      if (increase > 10) return 'bg-red-200';
      if (increase > 5) return 'bg-orange-100';
      return 'bg-orange-50';
    }

    // No change
    return 'bg-gray-50';
  };

  // Calculate SKU price change color
  const getSkuCellColor = (
    category: string,
    sku: string,
    currentTomaId: number,
    currentIndex: number,
    currentPrice: number
  ): string => {
    if (!data.skuMatrix) return 'bg-gray-50';

    // Find previous toma with this SKU
    for (let i = currentIndex - 1; i >= 0; i--) {
      const prevTomaId = data.tomas[i].tomaId;
      const prevSkus = data.skuMatrix[category]?.[prevTomaId];
      if (prevSkus) {
        const prevSku = prevSkus.find(s => s.sku === sku);
        if (prevSku) {
          if (currentPrice < prevSku.price) {
            const decrease = ((prevSku.price - currentPrice) / prevSku.price) * 100;
            if (decrease > 10) return 'bg-green-200';
            if (decrease > 5) return 'bg-green-100';
            return 'bg-green-50';
          } else if (currentPrice > prevSku.price) {
            const increase = ((currentPrice - prevSku.price) / prevSku.price) * 100;
            if (increase > 10) return 'bg-red-200';
            if (increase > 5) return 'bg-orange-100';
            return 'bg-orange-50';
          }
          return 'bg-gray-50';
        }
      }
    }

    return 'bg-blue-50'; // New SKU
  };

  // Get all SKUs for a category across all tomas
  const getSkusForCategory = (category: string): string[] => {
    if (!data.skuMatrix?.[category]) return [];
    const skus = new Set<string>();
    for (const tomaId of Object.keys(data.skuMatrix[category])) {
      const skuList = data.skuMatrix[category][parseInt(tomaId)];
      if (skuList) {
        skuList.forEach(s => skus.add(s.sku));
      }
    }
    return Array.from(skus).sort();
  };

  // Get SKU price for a specific toma
  const getSkuPrice = (category: string, sku: string, tomaId: number): number | null => {
    const skuList = data.skuMatrix?.[category]?.[tomaId];
    if (!skuList) return null;
    const skuData = skuList.find(s => s.sku === sku);
    return skuData?.price || null;
  };

  // Get SKU image (first available across all tomas)
  const getSkuImage = (category: string, sku: string): string | null => {
    if (!data.skuMatrix?.[category]) return null;
    for (const tomaId of Object.keys(data.skuMatrix[category])) {
      const skuList = data.skuMatrix[category][parseInt(tomaId)];
      if (skuList) {
        const skuData = skuList.find(s => s.sku === sku && s.imagen);
        if (skuData?.imagen) return skuData.imagen;
      }
    }
    return null;
  };

  // PT-#87 F2: todo conteo de la matriz con separador de miles es-AR ("1.380", no "1380").
  const fmtCount = (n: number): string => n.toLocaleString('es-AR');

  // Get the unit label for display
  const getCountLabel = (count: number): string => {
    const unitLabel = getUnitLabel(count, count !== 1);
    return `${fmtCount(count)} ${unitLabel}`; // PT-#87 PR-d: miles es-AR ("1.380", no "1380")
  };

  return (
    <div className="overflow-hidden">
      {/* Search bar if many categories */}
      {data.categories.length > 20 && (
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('statistics.category_price_trend.search_categories')}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            {t('statistics.category_price_trend.showing_categories', {
              showing: fmtCount(displayedCategories.length),
              total: fmtCount(filteredCategories.length)
            })}
          </p>
        </div>
      )}

      {/* Matrix Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="sticky left-0 z-20 bg-gray-50 px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px] border-r border-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                {t('statistics.category_price_trend.category_column')}
              </th>
              {data.tomas.map(toma => (
                <th
                  key={toma.tomaId}
                  className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px]"
                >
                  {formatDate(toma.fecha)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {displayedCategories.map((category, catIndex) => {
              const isExpanded = expandedCategories.has(category);
              const skus = expandSkus && data.skuMatrix ? getSkusForCategory(category) : [];
              const hasSkus = skus.length > 0;

              return (
                <React.Fragment key={category}>
                  {/* Category Row */}
                  <tr className="hover:bg-gray-50">
                    <td className="sticky left-0 z-20 bg-white px-4 py-2 text-sm font-medium text-gray-900 border-r border-gray-200 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                      <div className="flex items-center space-x-2">
                        {hasSkus && (
                          <button
                            onClick={() => toggleCategory(category)}
                            className="p-0.5 hover:bg-gray-200 rounded"
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-500" />
                            )}
                          </button>
                        )}
                        <span className={hasSkus ? 'font-semibold' : ''}>
                          {category}
                        </span>
                      </div>
                    </td>
                    {data.tomas.map((toma, tomaIndex) => {
                      const cell = data.matrix[category]?.[toma.tomaId];
                      const cellColor = getCellColor(category, toma.tomaId, tomaIndex);

                      return (
                        <td
                          key={toma.tomaId}
                          className={`px-3 py-2 text-center text-sm ${cellColor} transition-colors`}
                          title={cell ? getCountLabel(cell.count) : t('statistics.category_price_trend.no_data_cell')}
                        >
                          {cell ? (
                            <div>
                              <div className="font-medium text-gray-900">
                                {formatPrice(cell.value)}
                              </div>
                              <div className="text-[10px] text-gray-400">
                                ({fmtCount(cell.count)})
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>

                  {/* SKU Rows (expanded) - Only for SKUs mode */}
                  {isExpanded && hasSkus && skus.map((sku, skuIndex) => {
                    const skuImage = getSkuImage(category, sku);
                    return (
                    <tr key={`${category}-${sku}`} className="bg-gray-50/50">
                      <td className="sticky left-0 z-20 bg-gray-50 px-4 py-1.5 text-xs text-gray-600 border-r border-gray-200 pl-8 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]">
                        <div className="flex items-center space-x-2">
                          {skuImage ? (
                            <img
                              src={skuImage}
                              alt={sku}
                              className="w-6 h-6 object-cover rounded flex-shrink-0"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-6 h-6 bg-gray-200 rounded flex-shrink-0" />
                          )}
                          <span className="truncate">{sku}</span>
                        </div>
                      </td>
                      {data.tomas.map((toma, tomaIndex) => {
                        const price = getSkuPrice(category, sku, toma.tomaId);
                        const cellColor = price
                          ? getSkuCellColor(category, sku, toma.tomaId, tomaIndex, price)
                          : 'bg-gray-100/50';

                        return (
                          <td
                            key={toma.tomaId}
                            className={`px-3 py-1.5 text-center text-xs ${cellColor}`}
                          >
                            {price ? formatPrice(price) : '-'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Load More Button */}
      {filteredCategories.length > visibleCategories && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setVisibleCategories(prev => prev + 20)}
            className="px-4 py-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
          >
            {t('statistics.category_price_trend.load_more')} ({fmtCount(filteredCategories.length - visibleCategories)} {t('statistics.category_price_trend.remaining')})
          </button>
        </div>
      )}

      {/* Legend */}
      <div className="mt-4 flex items-center justify-center space-x-6 text-xs text-gray-500">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-green-100 rounded"></div>
          <span>{t('statistics.category_price_trend.legend_decrease')}</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-gray-50 rounded border border-gray-200"></div>
          <span>{t('statistics.category_price_trend.legend_no_change')}</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-orange-100 rounded"></div>
          <span>{t('statistics.category_price_trend.legend_increase')}</span>
        </div>
        {analysisLevel === 'skus' && expandSkus && (
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-blue-50 rounded"></div>
            <span>{t('statistics.category_price_trend.legend_new')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryPriceMatrix;
